import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Trash2, Plus, UserPlus, ShoppingCart } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/lib/settings";
import { todayISO } from "@/lib/period";
import { PageHeader } from "@/components/Bits";
import Modal from "@/components/Modal";

const CHANNELS = ["Walk-in", "WhatsApp", "Instagram", "Website", "Other"];
let customSeq = 0;

export default function NewSale() {
  const { isOwner } = useAuth();
  const nav = useNavigate();
  const settings = useSettings();
  const METHODS = settings?.payment_methods || ["Cash", "UPI", "Card", "Bank"];
  const [params] = useSearchParams();
  const [invDate, setInvDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [custom, setCustom] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [channel, setChannel] = useState("Walk-in");
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [custSearch, setCustSearch] = useState("");
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "", address: "", tax_id: "" });
  const [method, setMethod] = useState("UPI");
  const [payAmount, setPayAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const searchRef = useRef();

  useEffect(() => {
    api.get("/customers").then((r) => {
      setCustomers(r.data);
      const pre = params.get("customer");
      if (pre) setCustomer(r.data.find((c) => c.id === pre) || null);
    });
  }, [params]);

  const openCustom = () => setCustom({ description: "", hsn: "", qty: 1, rate: "", tax_rate: settings?.default_tax_rate ?? 5 });
  const addCustom = () => {
    customSeq += 1;
    setCart((c) => [...c, {
      sku_id: `custom-${customSeq}`, custom: true, name: custom.description.trim(), hsn: custom.hsn.trim(),
      color: "", size: "", qty: Math.max(1, parseInt(custom.qty || 1, 10)), rate: Number(custom.rate) || 0,
      discount: 0, tax_rate: Number(custom.tax_rate), max: 9999,
    }]);
    setCustom(null);
  };

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const r = await api.get(`/skus/search?q=${encodeURIComponent(q)}`);
      setResults(r.data);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const addToCart = (sku) => {
    if (sku.current_qty <= 0) { toast.error("Out of stock"); return; }
    setCart((c) => {
      const ex = c.find((x) => x.sku_id === sku.id);
      if (ex) {
        if (ex.qty >= sku.current_qty) { toast.error(`Only ${sku.current_qty} available`); return c; }
        return c.map((x) => x.sku_id === sku.id ? { ...x, qty: x.qty + 1 } : x);
      }
      return [...c, { sku_id: sku.id, style_code: sku.style_code, name: sku.style_name, color: sku.color, size: sku.size, qty: 1, rate: sku.selling_price, discount: 0, tax_rate: sku.tax_rate, max: sku.current_qty }];
    });
    setQ(""); setResults([]); searchRef.current?.focus();
  };

  const updateLine = (id, patch) => setCart((c) => c.map((x) => x.sku_id === id ? { ...x, ...patch } : x));
  const removeLine = (id) => setCart((c) => c.filter((x) => x.sku_id !== id));

  const totals = useMemo(() => {
    let sub = 0, disc = 0, tax = 0;
    cart.forEach((l) => {
      const gross = l.qty * l.rate;
      const taxable = gross - l.discount;
      sub += gross; disc += l.discount; tax += (taxable * l.tax_rate) / 100;
    });
    return { sub, disc, tax, grand: sub - disc + tax };
  }, [cart]);

  const filteredCust = customers.filter((c) => !custSearch || (c.name + c.phone).toLowerCase().includes(custSearch.toLowerCase()));

  const createCustomer = async () => {
    try {
      const r = await api.post("/customers", newCust);
      setCustomers((c) => [...c, r.data]);
      setCustomer(r.data);
      setShowNewCust(false);
      setNewCust({ name: "", phone: "", email: "", address: "", tax_id: "" });
      toast.success("Customer added");
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirm = async (payNow) => {
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    setBusy(true);
    const amt = payNow ? (payAmount === "" ? totals.grand : parseFloat(payAmount)) : 0;
    const payments = amt > 0 ? [{ amount: amt, method }] : [];
    try {
      const r = await api.post("/sales", {
        customer_id: customer?.id || null,
        channel,
        date: invDate,
        due_date: dueDate || null,
        notes,
        lines: cart.map((l) => l.custom
          ? { description: l.name, hsn: l.hsn, tax_rate: l.tax_rate, qty: l.qty, rate: l.rate, discount: l.discount }
          : { sku_id: l.sku_id, qty: l.qty, rate: l.rate, discount: l.discount }),
        payments,
        status: "Issued",
      });
      toast.success(`Invoice ${r.data.invoice_number} created`);
      nav(`/invoices/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Invoicing" title="New Invoice" subtitle="Add stock items or services, take payment now or on credit, and issue a GST invoice.">
        <button className="btn btn-ghost" onClick={openCustom} data-testid="add-custom-line"><Plus size={16} /> Service / other charge</button>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: search + cart */}
        <div className="lg:col-span-2 space-y-4">
          <div className="relative">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8C929B]" />
            <input ref={searchRef} className="field pl-11 py-3 text-base" placeholder="Scan barcode or search style / code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pos-search" autoFocus />
            {results.length > 0 && (
              <div className="absolute top-14 left-0 right-0 card shadow-xl z-40 max-h-96 overflow-auto" data-testid="pos-results">
                {results.map((s) => (
                  <button key={s.id} disabled={s.current_qty <= 0} onClick={() => addToCart(s)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#F4F0EA] disabled:opacity-40 text-left border-b border-[#F0EEE8] last:border-0" data-testid={`pos-result-${s.id}`}>
                    {s.image && <img src={s.image} alt="" className="w-9 h-11 object-cover rounded border border-[#E6E1D8]" />}
                    <div className="flex-1">
                      <div className="text-sm font-medium">{s.style_name}</div>
                      <div className="text-xs text-[#8C929B]">{s.color} / {s.size} · {s.style_code}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-num text-sm">{inr(s.selling_price)}</div>
                      <div className={`text-xs font-num ${s.current_qty <= 0 ? "text-[#A3411D]" : "text-[#8C929B]"}`}>{s.current_qty} in stock</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="card overflow-hidden">
            <table className="tbl">
              <thead>
                <tr><th>Item</th><th className="num" style={{ width: 80 }}>Qty</th><th className="num" style={{ width: 110 }}>Rate</th><th className="num" style={{ width: 100 }}>Disc</th><th className="num">Amount</th><th></th></tr>
              </thead>
              <tbody>
                {cart.map((l) => {
                  const amount = l.qty * l.rate - l.discount;
                  const total = amount + (amount * l.tax_rate) / 100;
                  return (
                    <tr key={l.sku_id} data-testid={`cart-line-${l.sku_id}`}>
                      <td>
                        <div className="font-medium">{l.name}</div>
                        <div className="text-xs text-[#8C929B]">{l.custom ? `Non-stock${l.hsn ? ` · HSN/SAC ${l.hsn}` : ""}` : `${l.color} / ${l.size}`} · {l.tax_rate}% tax</div>
                      </td>
                      <td className="num">
                        <input type="number" min="1" max={l.max} className="field py-1 px-2 w-16 text-right font-num" value={l.qty}
                          onChange={(e) => updateLine(l.sku_id, { qty: Math.max(1, Math.min(l.max, parseInt(e.target.value || "1", 10))) })} data-testid={`cart-qty-${l.sku_id}`} />
                      </td>
                      <td className="num">
                        <input type="number" className="field py-1 px-2 w-24 text-right font-num" value={l.rate} disabled={!isOwner && !l.custom}
                          onChange={(e) => updateLine(l.sku_id, { rate: parseFloat(e.target.value || "0") })} data-testid={`cart-rate-${l.sku_id}`} title={isOwner ? "Price override" : "Only owner can override price"} />
                      </td>
                      <td className="num">
                        <input type="number" className="field py-1 px-2 w-20 text-right font-num" value={l.discount}
                          onChange={(e) => updateLine(l.sku_id, { discount: parseFloat(e.target.value || "0") })} data-testid={`cart-disc-${l.sku_id}`} />
                      </td>
                      <td className="num font-num">{inr(total)}</td>
                      <td className="num"><button onClick={() => removeLine(l.sku_id)} className="text-[#A3411D] hover:opacity-70" data-testid={`cart-remove-${l.sku_id}`}><Trash2 size={16} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {cart.length === 0 && <div className="py-16 text-center text-[#8C929B] text-sm"><ShoppingCart size={28} className="mx-auto mb-3 opacity-40" />Nothing added yet — search stock above, or add a service / other charge</div>}
          </div>

          <div className="card p-5 grid grid-cols-3 gap-4">
            <div><label className="label">Invoice date</label><input type="date" className="field" value={invDate} max={todayISO()} onChange={(e) => setInvDate(e.target.value)} data-testid="inv-date" /></div>
            <div><label className="label">Due date (credit)</label><input type="date" className="field" value={dueDate} min={invDate} onChange={(e) => setDueDate(e.target.value)} data-testid="inv-due" />
              <div className="text-xs text-[#8C929B] mt-1">Blank = {settings?.default_due_days ?? 15} days if unpaid</div></div>
            <div><label className="label">Notes on invoice</label><input className="field" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="inv-notes" /></div>
          </div>
        </div>

        {/* Right: checkout */}
        <div className="space-y-4">
          <div className="card p-5">
            <div className="eyebrow mb-2">Customer</div>
            {customer ? (
              <div className="flex items-center justify-between">
                <div><div className="font-medium">{customer.name}</div><div className="text-xs text-[#8C929B] font-num">{customer.phone}</div></div>
                <button className="btn btn-ghost btn-sm" onClick={() => setCustomer(null)}>Change</button>
              </div>
            ) : (
              <div>
                <div className="relative mb-2">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C929B]" />
                  <input className="field pl-8 py-2" placeholder="Search or leave as Walk-in" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="pos-cust-search" />
                </div>
                {custSearch && (
                  <div className="max-h-40 overflow-auto border border-[#E6E1D8] rounded-lg mb-2">
                    {filteredCust.slice(0, 6).map((c) => (
                      <button key={c.id} onClick={() => { setCustomer(c); setCustSearch(""); }} className="w-full text-left px-3 py-2 hover:bg-[#F4F0EA] text-sm" data-testid={`pos-cust-${c.id}`}>
                        {c.name} <span className="text-[#8C929B] font-num">· {c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
                <button className="btn btn-ghost btn-sm w-full justify-center" onClick={() => setShowNewCust(true)} data-testid="pos-new-cust"><UserPlus size={14} /> New customer</button>
              </div>
            )}
          </div>

          <div className="card p-5">
            <div className="eyebrow mb-2">Sale channel</div>
            <div className="grid grid-cols-3 gap-1.5">
              {CHANNELS.map((c) => (
                <button key={c} onClick={() => setChannel(c)} data-testid={`channel-${c}`}
                  className={`px-2 py-1.5 rounded-lg text-xs transition-colors ${channel === c ? "bg-[#0E1B2E] text-white" : "bg-[#F4F0EA] text-[#5C626A] hover:bg-[#EDE8DF]"}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="card p-5">
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between"><span className="text-[#5C626A]">Subtotal</span><span className="font-num">{inr(totals.sub)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C626A]">Discount</span><span className="font-num text-[#A3411D]">−{inr(totals.disc)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C626A]">Tax (GST)</span><span className="font-num">{inr(totals.tax)}</span></div>
              <div className="flex justify-between pt-2 border-t border-[#E6E1D8] text-base"><span className="font-semibold">Grand Total</span><span className="font-num font-semibold text-[#0E1B2E]" data-testid="pos-grand-total">{inr(totals.grand)}</span></div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="label">Method</label>
                <select className="field py-2" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pos-method">
                  {METHODS.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Amount paid</label>
                <input type="number" className="field py-2 font-num" placeholder={totals.grand.toFixed(2)} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pos-pay-amount" />
              </div>
            </div>

            <button className="btn btn-primary w-full justify-center mb-2" disabled={busy || !cart.length} onClick={() => confirm(true)} data-testid="pos-confirm-pay">
              {busy ? "Processing…" : `Take payment & issue · ${inr(totals.grand)}`}
            </button>
            <button className="btn btn-ghost w-full justify-center" disabled={busy || !cart.length} onClick={() => confirm(false)} data-testid="pos-pay-later">
              Issue on credit (pay later)
            </button>
          </div>
        </div>
      </div>

      <Modal open={!!custom} onClose={() => setCustom(null)} title="Service / Other Charge" testid="custom-line-modal">
        {custom && (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className="label">Description</label><input className="field" value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} placeholder="e.g. Alteration, Delivery charge, Gift wrap" data-testid="custom-desc" autoFocus /></div>
            <div><label className="label">HSN / SAC</label><input className="field font-num" value={custom.hsn} onChange={(e) => setCustom({ ...custom, hsn: e.target.value })} placeholder="Optional" /></div>
            <div><label className="label">GST</label>
              <select className="field" value={custom.tax_rate} onChange={(e) => setCustom({ ...custom, tax_rate: e.target.value })}>
                {[...new Set([0, ...(settings?.tax_rates || [5, 12, 18])])].sort((a, b) => a - b).map((r) => <option key={r} value={r}>{r}%</option>)}
              </select></div>
            <div><label className="label">Qty</label><input type="number" min="1" className="field font-num" value={custom.qty} onChange={(e) => setCustom({ ...custom, qty: e.target.value })} /></div>
            <div><label className="label">Rate (before GST)</label><input type="number" min="0" className="field font-num" value={custom.rate} onChange={(e) => setCustom({ ...custom, rate: e.target.value })} data-testid="custom-rate" /></div>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost" onClick={() => setCustom(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={addCustom} disabled={!custom.description.trim() || !(Number(custom.rate) > 0)} data-testid="custom-add">Add to invoice</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showNewCust} onClose={() => setShowNewCust(false)} title="New Customer" testid="pos-newcust-modal">
        <div className="space-y-4">
          {["name", "phone", "email", "address", "tax_id"].map((f) => (
            <div key={f}><label className="label">{f === "tax_id" ? "Tax ID / GSTIN" : f}</label>
              <input className="field" value={newCust[f]} onChange={(e) => setNewCust({ ...newCust, [f]: e.target.value })} data-testid={`newcust-${f}`} /></div>
          ))}
          <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={() => setShowNewCust(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={createCustomer} disabled={!newCust.name} data-testid="newcust-save">Add</button></div>
        </div>
      </Modal>
    </div>
  );
}
